'use client';

import { Button, Flex, Icon, Spinner, Text } from '@chakra-ui/react';
import { AiOutlineUser } from 'react-icons/ai';
import { useCallback, useContext, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ErrorMessage from '@/components/ErrorMessage';
import FormInput from '@/components/Form/form-input';
import { api } from '@/services/api';
import { FaCheckCircle } from 'react-icons/fa';
import { FaChevronLeft } from 'react-icons/fa6';

type FormObject = {
  email: string;
};

const schema = z.object({
  email: z.email('E-mail inválido').min(1, { message: 'E-mail é obrigatório' }),
});

const UserResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  const { handleChangeStatus } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormObject>({ resolver: zodResolver(schema) });

  const onSubmit = useCallback(
    async (data: FormObject) => {
      setIsLoading(true);
      try {
        const response = await api.post('/customer/password/reset', {
          email: data.email,
        });

        setPasswordResetSuccess(response.data);

        reset();
      } catch (error) {
        const e = error as { message: string };

        setErrorMessage(e.message);
      } finally {
        setIsLoading(false);
      }
    },
    [reset],
  );

  if (passwordResetSuccess)
    return (
      <Flex flexDir="column" gap="24px" align="center" py="8px">
        {/* Icon */}
        <Flex
          w="72px"
          h="72px"
          borderRadius="full"
          bg="rgba(46,204,113,0.1)"
          border="2px solid rgba(46,204,113,0.25)"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon w="32px" h="32px" color="#2ecc71">
            <FaCheckCircle />
          </Icon>
        </Flex>

        {/* Text */}
        <Flex flexDir="column" align="center" gap="8px">
          <Text
            fontSize="20px"
            fontWeight="700"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
          >
            E-mail enviado!
          </Text>
          <Text
            fontSize="13px"
            textAlign="center"
            color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}
            lineHeight="1.6"
          >
            Enviámos instruções para redefinir a sua palavra-passe. Verifique a
            sua caixa de entrada.
          </Text>
        </Flex>

        {/* Button */}
        <Button
          w="full"
          type="button"
          h="44px"
          borderRadius="full"
          bg="#00B0CA"
          color="white"
          fontWeight="700"
          fontSize="15px"
          boxShadow="0 4px 20px rgba(0,176,202,0.3)"
          onClick={() => handleChangeStatus('signin')}
          _hover={{ bg: '#009AB5' }}
          transition="all 0.2s"
        >
          Iniciar Sessão
        </Button>
      </Flex>
    );

  return (
    <Flex
      flexDir="column"
      gap="20px"
      as="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Flex align="center" gap="8px">
        <Flex
          as="button"
          onClick={() => handleChangeStatus('signin')}
          w="28px"
          h="28px"
          align="center"
          justify="center"
          borderRadius="8px"
          color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}
          _hover={{ bg: 'rgba(0,176,202,0.08)', color: '#00B0CA' }}
          transition="all 0.15s"
          flexShrink={0}
        >
          <FaChevronLeft size={13} />
        </Flex>
        <Text
          fontSize="18px"
          fontWeight="700"
          color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
        >
          Recuperar Palavra-passe
        </Text>
      </Flex>
      <Flex>
        <FormInput
          placeholder="E-mail"
          width="100%"
          error={errors.email?.message}
          {...register('email')}
          icon={<AiOutlineUser />}
        />
      </Flex>

      <Button
        w="full"
        type="submit"
        h="44px"
        borderRadius="full"
        bg="#00B0CA"
        color="white"
        fontWeight="700"
        fontSize="15px"
        boxShadow="0 4px 20px rgba(0,176,202,0.3)"
        disabled={isLoading}
        _hover={{ bg: '#009AB5' }}
        _disabled={{ opacity: 0.5 }}
        transition="all 0.2s"
      >
        {isLoading ? <Spinner size="sm" color="white" /> : 'Recuperar'}
      </Button>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </Flex>
  );
};

export default UserResetPassword;
